import { observable, WritableObservable } from "micro-observables";
import { OnboardingManager } from "@domain/onboarding/onboarding-manager";
import {
	OnboardingGetStartedStep,
	onboardingGetStartedSteps,
	OnboardingStepsStatus,
	OnboardingStepStatus,
} from "@domain/onboarding/onboarding";
import { GetStartedStepStatus } from "@domain/get-started/get-started";
import { isNotNil } from "@utils/assert";
import { isNil } from "lodash";
import { UserManager } from "@domain/user/user-manager";
import { User } from "@domain/user/user";

const TIMER_DELAY_MS = 3600000; // 1 hour

type GetStartedStepsStatus = Record<keyof typeof OnboardingGetStartedStep, GetStartedStepStatus>;

enum GetStartedVisibility {
	Hidden = "Hidden",
	Visible = "Visible",
	Timed = "Timed",
}

export class GetStartedManager {
	private _getStartedStepsStatus: GetStartedStepsStatus | undefined = undefined;
	private _timer: NodeJS.Timeout | undefined = undefined;

	private readonly _completedCount = observable(0);
	completedCount = this._completedCount.readOnly();

	private readonly _exercisesStepStatus = observable(GetStartedStepStatus.Default);
	exercisesStepStatus = this._exercisesStepStatus.readOnly();

	private readonly _programsStepStatus = observable(GetStartedStepStatus.Default);
	programsStepStatus = this._programsStepStatus.readOnly();

	private readonly _patientsStepStatus = observable(GetStartedStepStatus.Default);
	patientsStepStatus = this._patientsStepStatus.readOnly();

	private readonly _shouldDisplayGetStartedTab = observable(false);
	shouldDisplayGetStartedTab = this._shouldDisplayGetStartedTab.readOnly();

	shouldDisplayGetStartedCompletedDialog = observable(false);

	private readonly _currentUser = observable<User | null>(null);

	constructor(
		private readonly onboardingManager: OnboardingManager,
		userManager: UserManager,
	) {
		onboardingManager.onboardingStepsStatus.subscribe(onboardingStepsStatus => {
			if (this._currentUser.get() === null) return;

			const stepsStatus = getGetStartedStepsStatus(onboardingStepsStatus);

			if (
				isNil(this._getStartedStepsStatus) ||
				!areGetStartedStepsStatusEqual(this._getStartedStepsStatus, stepsStatus)
			) {
				const newCompletedCount = getCompletedStepsCount(stepsStatus);
				const oldCompletedCount = isNil(this._getStartedStepsStatus) ? newCompletedCount : this._completedCount.get();
				const getStartedVisibility = getGetStartedVisibility(oldCompletedCount, newCompletedCount);

				this._getStartedStepsStatus = stepsStatus;

				if (getStartedVisibility === GetStartedVisibility.Timed) {
					this._timer = setTimeout(() => {
						updateObservable(this._shouldDisplayGetStartedTab, false);
						this._timer = undefined;
					}, TIMER_DELAY_MS);
				}

				updateObservable(this._shouldDisplayGetStartedTab, getStartedVisibility !== GetStartedVisibility.Hidden);
				updateObservable(
					this.shouldDisplayGetStartedCompletedDialog,
					getStartedVisibility === GetStartedVisibility.Timed,
				);
				updateObservable(this._completedCount, newCompletedCount);
				updateObservable(this._exercisesStepStatus, stepsStatus[OnboardingGetStartedStep.GET_STARTED_EXERCISES]);
				updateObservable(this._programsStepStatus, stepsStatus[OnboardingGetStartedStep.GET_STARTED_PROGRAMS]);
				updateObservable(this._patientsStepStatus, stepsStatus[OnboardingGetStartedStep.GET_STARTED_PATIENTS]);
			}
		});

		userManager.user.subscribe(async user => {
			const currentUser = this._currentUser.get();
			if (isNotNil(currentUser) && currentUser.id !== user?.id) this.reset();
			updateObservable(this._currentUser, user);
		});
	}

	async tryMarkExercisesStepAsCompleted(): Promise<void> {
		await this.tryPassStepAsCompleted(OnboardingGetStartedStep.GET_STARTED_EXERCISES);
	}

	async tryMarkProgramsStepAsCompleted(): Promise<void> {
		await this.tryPassStepAsCompleted(OnboardingGetStartedStep.GET_STARTED_PROGRAMS);
	}

	async tryMarkPatientsStepAsCompleted(): Promise<void> {
		await this.tryPassStepAsCompleted(OnboardingGetStartedStep.GET_STARTED_PATIENTS);
	}

	private readonly tryPassStepAsCompleted = async (step: OnboardingGetStartedStep): Promise<void> => {
		if (isNotNil(this._getStartedStepsStatus) && this._getStartedStepsStatus[step] === GetStartedStepStatus.Default) {
			try {
				await this.onboardingManager.passStepOnboarding(step);
			} catch (error) {
				console.error(`Failed to pass get-started step ${step}:`, error);
			}
		}
	};

	private readonly reset = (): void => {
		this._getStartedStepsStatus = undefined;

		if (isNotNil(this._timer)) {
			clearTimeout(this._timer);
			this._timer = undefined;
		}
	};
}
const getGetStartedVisibility = (oldCompletedCount: number, newCompletedCount: number): GetStartedVisibility => {
	if (newCompletedCount < onboardingGetStartedSteps.length) {
		return GetStartedVisibility.Visible;
	}
	if (oldCompletedCount < onboardingGetStartedSteps.length) {
		return GetStartedVisibility.Timed;
	}
	return GetStartedVisibility.Hidden;
};

const areGetStartedStepsStatusEqual = (a: GetStartedStepsStatus, b: GetStartedStepsStatus): boolean =>
	onboardingGetStartedSteps.every(step => a[step] === b[step]);

const updateObservable = <T>(writableObservable: WritableObservable<T>, value: T): void => {
	if (writableObservable.get() !== value) {
		writableObservable.set(value);
	}
};

const getCompletedStepsCount = (getStartedStepsStatus: GetStartedStepsStatus): number =>
	onboardingGetStartedSteps.reduce(
		(count, step) => (getStartedStepsStatus[step] === GetStartedStepStatus.Completed ? count + 1 : count),
		0,
	);

const getGetStartedStepsStatus = (
	onboardingStepsStatus: OnboardingStepsStatus | undefined,
): Record<OnboardingGetStartedStep, GetStartedStepStatus> =>
	Object.fromEntries(
		onboardingGetStartedSteps.map(step => [
			step,
			onboardingStepsStatus?.[step] === OnboardingStepStatus.DISMISSED
				? GetStartedStepStatus.Completed
				: GetStartedStepStatus.Default,
		]),
	) as Record<OnboardingGetStartedStep, GetStartedStepStatus>;
